import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface StaffRole {
  id: string;
  user_id: string;
  role: 'support' | 'crm' | 'superadmin';
  created_at: string;
  updated_at: string;
}

export const useStaffRoles = () => {
  const { user } = useAuth();
  const [roles, setRoles] = useState<StaffRole[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setRoles([]);
      setLoading(false);
      return;
    }

    const fetchRoles = async () => {
      try {
        const { data, error } = await supabase
          .from('staff_roles')
          .select('*')
          .eq('user_id', user.id);

        if (error) {
          console.error('Error fetching staff roles:', error);
          setRoles([]);
        } else {
          const formattedRoles = (data || []).map(role => ({
            ...role,
            role: role.role as 'support' | 'crm' | 'superadmin'
          }));
          setRoles(formattedRoles);
        }
      } catch (error) {
        console.error('Error fetching staff roles:', error);
        setRoles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, [user]);

  const hasRole = (role: string) => {
    return roles.some(r => r.role === role);
  };

  const isSupport = () => hasRole('support') || hasRole('superadmin');
  const isCRM = () => hasRole('crm') || hasRole('superadmin');
  const isSuperAdmin = () => hasRole('superadmin');
  const isStaff = () => roles.length > 0;

  return {
    roles,
    loading,
    hasRole,
    isSupport,
    isCRM,
    isSuperAdmin,
    isStaff
  };
};